/* eslint-disable no-console */
import * as Styled from './AttendeeActions.styles';

import {
	ArrowRightLeft,
	Clock,
	DollarSign,
	History,
	Loader2,
	Mail,
	Pin,
	Printer,
	Repeat,
	RotateCcw,
	Send,
	StickyNote,
	TicketCheck,
	X,
} from 'lucide-react';
import { FC, Fragment, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import {
	useCancelTicket,
	useUncancelTicket,
	useUndoCheckIn,
	useValidateTicket,
} from 'features/CustomFieldsResponses/hooks/useEventCustomFields';

import { ConfirmDialog } from './ConfirmDialog';
import { FanNotesModal } from '../FanNotesModal';
import { IdParamsType } from 'utils/types';
import { canUndoCheckIn } from 'utils/utils';
import { PunchupButton } from 'features/common/components/Form/PunchupButton/PunchupButton';
import { PunchupModal } from 'features/common/components/Form/PunchupModal/PunchupModal';
import ReactDOM from 'react-dom';
import { ResponsesData } from '../../types';
import { SwitchTicketDelegator } from './SwitchTicketDelegator';
import { delegateRefundToHost } from 'features/PunchupPages/CustomFieldsResponse/components/AttendeeActions/delegateRefundToHost';
import { getData } from 'services/api';
import { isCanceledStatus } from 'features/PunchupPages/CustomFieldsResponse/components/PunchupResponsesTable/statusColors';
import { postToast } from 'features/common/utils/postFlash';
import { useParams } from 'react-router-dom';
import { usePinnedActions } from 'features/CustomFieldsResponses/hooks/usePinnedActions';
import { useQuery } from '@tanstack/react-query';
import { useRefundTicket } from 'features/CustomFieldsResponses/hooks/useEventCustomFields';
import { useResendEmail } from 'features/CustomFieldsResponses/hooks/useEventCustomFields';
import { useTicketHistory } from 'features/CustomFieldsResponses/hooks/useEventCustomFields';
import { useTransferTicket } from 'features/CustomFieldsResponses/hooks/useEventCustomFields';
import { useTranslation } from 'react-i18next';

interface AttendeeActionsProps {
	data: ResponsesData;
	transactionTickets: Record<string, number[]>;
}

type ActionId =
	| 'print'
	| 'resend'
	| 'transfer'
	| 'switch'
	| 'refund'
	| 'history'
	| 'notes'
	| 'validate'
	| 'undo-check-in'
	| 'cancel'
	| 'uncancel';

interface ActionDef {
	id: ActionId;
	label: string;
	icon: ReactNode;
	onActivate: () => void;
	visible: boolean;
	disabled?: boolean;
	pinnable: boolean;
	destructive?: boolean;
}

const DROPDOWN_WIDTH = 224;
const DROPDOWN_HEIGHT = 360;

export const AttendeeActions: FC<AttendeeActionsProps> = ({ data, transactionTickets }) => {
	const { t } = useTranslation();
	const { id: eventId } = useParams<IdParamsType>();
	const { isPinned, togglePin } = usePinnedActions(eventId);

	const btnRef = useRef<HTMLButtonElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);

	const [isDownloading, setIsDownloading] = useState(false);
	const printFrameRef = useRef<HTMLIFrameElement | null>(null);
	const printBlobUrlRef = useRef<string | null>(null);
	const [open, setOpen] = useState(false);
	const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

	const [showResendModal, setShowResendModal] = useState(false);
	const [showTransferModal, setShowTransferModal] = useState(false);
	const [transferEmail, setTransferEmail] = useState('');
	const [showHistoryModal, setShowHistoryModal] = useState(false);
	const [showCancelModal, setShowCancelModal] = useState(false);
	const [showUncancelModal, setShowUncancelModal] = useState(false);
	const [showRefundModal, setShowRefundModal] = useState(false);
	const [showValidateModal, setShowValidateModal] = useState(false);
	const [showUndoCheckInModal, setShowUndoCheckInModal] = useState(false);
	const [showNotesModal, setShowNotesModal] = useState(false);
	const [refundReason, setRefundReason] = useState('');
	const [delegatingSwitch, setDelegatingSwitch] = useState(false);
	const { resendEmail, isLoading: loadingResend } = useResendEmail({
		onSuccess: () => setShowResendModal(false),
	});

	const { transferTicket, isLoading: loadingTransfer } = useTransferTicket({
		onSuccess: () => {
			setShowTransferModal(false);
			setTransferEmail('');
		},
	});

	const { cancelTicket, isLoading: loadingCancel } = useCancelTicket({
		onSuccess: () => setShowCancelModal(false),
	});

	const { uncancelTicket, isLoading: loadingUncancel } = useUncancelTicket({
		onSuccess: () => setShowUncancelModal(false),
	});

	const { refundTicket, isLoading: loadingRefund } = useRefundTicket({
		onSuccess: () => {
			setShowRefundModal(false);
			setRefundReason('');
		},
	});

	const { validateTicket, isLoading: loadingValidate } = useValidateTicket({
		onSettled: () => setShowValidateModal(false),
	});

	const { undoCheckIn, isLoading: loadingUndoCheckIn } = useUndoCheckIn({
		onSuccess: () => setShowUndoCheckInModal(false),
	});

	useEffect(() => {
		if (!open) return;
		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as Node;
			if (btnRef.current?.contains(target)) return;
			if (dropdownRef.current?.contains(target)) return;
			setOpen(false);
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [open]);

	useEffect(() => {
		if (open && btnRef.current) {
			const rect = btnRef.current.getBoundingClientRect();
			let top = rect.bottom + 6;
			let left = rect.right - DROPDOWN_WIDTH;

			if (left + DROPDOWN_WIDTH > window.innerWidth) {
				left = window.innerWidth - DROPDOWN_WIDTH - 8;
			}
			if (left < 8) left = 8;
			if (top + DROPDOWN_HEIGHT > window.innerHeight) {
				top = rect.top - DROPDOWN_HEIGHT - 6;
			}
			if (top < 8) top = 8;

			setMenuPos({ top, left });
		}
	}, [open]);

	const status = data.status?.toLowerCase() ?? '';
	const isRefunded = status === 'refunded';
	const isSwitchedTo = status === 'switched to';
	// `External Canceled` (an import canceled or voided, TIC-952) comes back with Un-Cancel too.
	const isCanceled = isCanceledStatus(status);
	const isLive = !isRefunded && !isSwitchedTo && !isCanceled;
	// Undo is admin/editor only: any other role gets a 401 the api client treats as an expired session.
	const canUndo = data.is_validated && canUndoCheckIn(localStorage.getItem('accessToken'));

	useEffect(() => {
		return () => {
			if (printBlobUrlRef.current) {
				window.URL.revokeObjectURL(printBlobUrlRef.current);
				printBlobUrlRef.current = null;
			}
			if (printFrameRef.current) {
				printFrameRef.current.remove();
				printFrameRef.current = null;
			}
		};
	}, []);

	const handlePrintTicket = async () => {
		if (isDownloading) return;
		setIsDownloading(true);
		try {
			const blob = await getData(`/download/pdf_ticket/${data.ticket_id}`, {
				responseType: 'blob',
				headers: { Accept: 'application/pdf' },
			});

			if (printBlobUrlRef.current) {
				window.URL.revokeObjectURL(printBlobUrlRef.current);
			}
			if (printFrameRef.current) {
				printFrameRef.current.remove();
			}

			const url = window.URL.createObjectURL(blob);
			printBlobUrlRef.current = url;

			const iframe = document.createElement('iframe');
			iframe.style.position = 'fixed';
			iframe.style.right = '0';
			iframe.style.bottom = '0';
			iframe.style.width = '0';
			iframe.style.height = '0';
			iframe.style.border = '0';
			iframe.setAttribute('aria-hidden', 'true');
			iframe.src = url;
			iframe.onload = () => {
				try {
					iframe.contentWindow?.focus();
					iframe.contentWindow?.print();
				} catch (err) {
					postToast('error', 'Failed to open print dialog');
				}
			};
			document.body.appendChild(iframe);
			printFrameRef.current = iframe;
		} catch (error) {
			postToast('error', 'Failed to load ticket PDF');
		} finally {
			setIsDownloading(false);
		}
	};

	const actions: ActionDef[] = useMemo(() => {
		const print: ActionDef = {
			id: 'print',
			label: t('attendee-actions-print-pdf', 'Print ticket'),
			icon: isDownloading ? (
				<Styled.SpinningIcon>
					<Loader2 size={16} />
				</Styled.SpinningIcon>
			) : (
				<Printer size={16} />
			),
			onActivate: () => {
				handlePrintTicket();
			},
			visible: true,
			disabled: isDownloading,
			pinnable: true,
		};

		const resend: ActionDef = {
			id: 'resend',
			label: t('attendee-actions-resend-ticket'),
			icon: <Mail size={16} />,
			onActivate: () => setShowResendModal(true),
			visible: true,
			pinnable: true,
		};

		const history: ActionDef = {
			id: 'history',
			label: 'View History',
			icon: <History size={16} />,
			onActivate: () => setShowHistoryModal(true),
			visible: true,
			pinnable: true,
		};

		if (isSwitchedTo) {
			return [print, history];
		}

		if (isRefunded) {
			return [print, resend, history];
		}

		const list: ActionDef[] = [
			print,
			resend,
			{
				id: 'transfer',
				label: t('attendee-actions-transfer-ticket'),
				icon: <Send size={16} />,
				onActivate: () => setShowTransferModal(true),
				visible: true,
				pinnable: true,
			},
			{
				id: 'switch',
				label: t('attendee-actions-switch-ticket'),
				icon: <Repeat size={16} />,
				onActivate: () => setDelegatingSwitch(true),
				visible: true,
				pinnable: true,
			},
			{
				id: 'refund',
				label: t('attendee-actions-refund-ticket'),
				icon: <DollarSign size={16} />,
				onActivate: () => {
					if (!delegateRefundToHost(data, eventId)) setShowRefundModal(true);
				},
				visible: true,
				pinnable: true,
			},
			{
				id: 'validate',
				label: data.is_validated
					? t('attendee-actions-revalidate-ticket', 'Re-validate ticket')
					: t('attendee-actions-validate-ticket', 'Validate ticket'),
				icon: <TicketCheck size={16} />,
				onActivate: () => setShowValidateModal(true),
				visible: isLive,
				pinnable: true,
			},
			{
				id: 'undo-check-in',
				label: t('attendee-actions-undo-check-in', 'Undo check-in'),
				icon: <RotateCcw size={16} />,
				onActivate: () => setShowUndoCheckInModal(true),
				visible: canUndo,
				pinnable: false,
			},
			history,
			{
				id: 'notes',
				label: 'Fan Notes',
				icon: <StickyNote size={16} />,
				onActivate: () => setShowNotesModal(true),
				visible: true,
				pinnable: true,
			},
		];

		if (isCanceled) {
			list.push({
				id: 'uncancel',
				label: t('attendee-actions-uncancel-ticket', 'Un-Cancel Ticket'),
				icon: <Repeat size={16} />,
				onActivate: () => setShowUncancelModal(true),
				visible: true,
				pinnable: false,
			});
		} else {
			list.push({
				id: 'cancel',
				label: t('attendee-actions-cancel-ticket'),
				icon: <X size={16} />,
				onActivate: () => setShowCancelModal(true),
				visible: true,
				pinnable: false,
				destructive: true,
			});
		}

		return list;
	}, [t, isDownloading, isSwitchedTo, isRefunded, isCanceled, isLive, data, eventId, canUndo]);

	const visibleActions = actions.filter((a) => a.visible);
	const pinnedQuick = visibleActions.filter((a) => a.pinnable && isPinned(a.id));
	const dropdownItems = visibleActions;

	const isTerminal = (id: ActionId) => id === 'cancel' || id === 'uncancel';

	return (
		<Styled.Container>
			{pinnedQuick.map((action) => (
				<Styled.MenuButton
					key={action.id}
					onClick={action.onActivate}
					disabled={action.disabled}
					title={action.label}
					aria-label={action.label}
				>
					{action.icon}
				</Styled.MenuButton>
			))}

			<Styled.MenuButton ref={btnRef} onClick={() => setOpen((v) => !v)} aria-label={t('actions')}>
				<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<circle cx="12" cy="5" r="1.5" />
					<circle cx="12" cy="12" r="1.5" />
					<circle cx="12" cy="19" r="1.5" />
				</svg>
			</Styled.MenuButton>

			{open &&
				ReactDOM.createPortal(
					<Styled.PortalDropdown ref={dropdownRef} style={{ top: menuPos.top, left: menuPos.left }}>
						{dropdownItems.map((action, idx) => {
							const ItemEl = action.destructive ? Styled.ItemDestructive : Styled.Item;
							const previousIsTerminal = idx > 0 && isTerminal(dropdownItems[idx - 1].id);
							const showSeparator = isTerminal(action.id) && idx > 0 && !previousIsTerminal;
							const pinned = isPinned(action.id);
							return (
								<Fragment key={action.id}>
									{showSeparator && <Styled.Separator />}
									<Styled.ItemRow>
										<ItemEl
											/* onClick, not onMouseDown: opening a modal on mousedown mounts it while the
											   button is still pressed, so the release retargets onto the fresh modal (e.g. its
											   Cancel button under the pointer) and dismisses it — the "blink". onClick defers
											   activation until the gesture completes. Mirrors the green AttendeeActions. */
											onClick={() => {
												if (action.disabled) return;
												action.onActivate();
												setOpen(false);
											}}
											disabled={action.disabled}
										>
											<Styled.ItemIcon>{action.icon}</Styled.ItemIcon>
											<Styled.ItemLabel>{action.label}</Styled.ItemLabel>
										</ItemEl>
										{action.pinnable && (
											<Styled.PinToggle
												type="button"
												active={pinned}
												onMouseDown={(e) => {
													e.stopPropagation();
													e.preventDefault();
													togglePin(action.id);
												}}
												title={pinned ? 'Unpin from quick access' : 'Pin to quick access'}
												aria-label={pinned ? 'Unpin action' : 'Pin action'}
												aria-pressed={pinned}
											>
												<Pin size={14} fill={pinned ? 'currentColor' : 'none'} />
											</Styled.PinToggle>
										)}
									</Styled.ItemRow>
								</Fragment>
							);
						})}
					</Styled.PortalDropdown>,
					document.body
				)}

			{showResendModal && (
				<PunchupModal title="Resend Order Email">
					<p
						style={{
							margin: '24px auto',
							textAlign: 'center',
							wordBreak: 'break-word',
							whiteSpace: 'normal',
							maxWidth: 340,
						}}
					>
						Remember: this email will resend the entire order. If a customer has multiple tickets in the same order and
						requests a resend, you only need to resend it once.
					</p>
					<div style={{ display: 'flex', justifyContent: 'center', gap: 16 }}>
						<PunchupButton
							variant="secondary"
							text="Cancel"
							onClick={() => setShowResendModal(false)}
							disabled={loadingResend}
						/>
						<PunchupButton
							variant="primary"
							text={loadingResend ? 'Sending...' : 'Send'}
							onClick={() => resendEmail(data.order_id)}
							disabled={loadingResend}
						/>
					</div>
				</PunchupModal>
			)}
			{showTransferModal && (
				<PunchupModal title="Transfer Tickets">
					<div style={{ margin: '16px 0', color: '#6b7280', fontSize: 16 }}>
						You are about to transfer the following ticket:
					</div>
					<div
						style={{
							background: '#f7f8fa',
							borderRadius: 12,
							padding: 16,
							marginBottom: 24,
							fontWeight: 600,
							fontSize: 18,
						}}
					>
						{data.ticket_types_string}
						<div style={{ fontWeight: 400, fontSize: 14, color: '#6b7280' }}>{data.full_name}</div>
					</div>
					<div style={{ fontWeight: 500, marginBottom: 8 }}>Recipient Email</div>
					<input
						type="email"
						placeholder="Enter recipient's email"
						value={transferEmail}
						onChange={(e) => setTransferEmail(e.target.value)}
						style={{
							width: '100%',
							padding: '12px',
							borderRadius: 8,
							border: '1px solid #e0e0e0',
							marginBottom: 24,
							fontSize: 16,
						}}
					/>
					<div style={{ display: 'flex', justifyContent: 'center', gap: 16 }}>
						<PunchupButton
							variant="secondary"
							text="Cancel"
							onClick={() => {
								setShowTransferModal(false);
								setTransferEmail('');
							}}
							disabled={loadingTransfer}
						/>
						<PunchupButton
							variant="primary"
							text={loadingTransfer ? 'Transferring...' : 'Transfer Tickets'}
							onClick={() => transferTicket({ ticketId: data.ticket_id, recipientEmail: transferEmail })}
							disabled={loadingTransfer || !/^\S+@\S+\.\S+$/.test(transferEmail)}
						/>
					</div>
				</PunchupModal>
			)}
			{showHistoryModal && (
				<PunchupModal title="Ticket History" wide={true}>
					<div
						style={{
							width: '100%',
							maxWidth: '100%',
							maxHeight: '80vh',
						}}
					>
						<HistoryModalContent ticketId={data.ticket_id} onClose={() => setShowHistoryModal(false)} />
					</div>
				</PunchupModal>
			)}
			{showCancelModal && data.status?.toLowerCase() !== 'canceled' && (
				<PunchupModal title={t('attendee-actions-cancel-ticket', 'Cancel Ticket')}>
					<p
						style={{
							margin: '24px auto',
							textAlign: 'center',
							wordBreak: 'break-word',
							whiteSpace: 'normal',
							maxWidth: 340,
							minHeight: 48,
							overflowWrap: 'break-word',
						}}
					>
						{t(
							'attendee-actions-cancel-confirm',
							'Are you sure you want to cancel this ticket? This action cannot be undone.'
						)}
					</p>
					<div style={{ display: 'flex', justifyContent: 'center', gap: 16 }}>
						<PunchupButton
							variant="secondary"
							text={t('cancel', 'Cancel')}
							onClick={() => setShowCancelModal(false)}
							disabled={loadingCancel}
						/>
						<PunchupButton
							variant="primary"
							text={loadingCancel ? t('cancelling', 'Cancelling...') : t('confirm', 'Confirm')}
							onClick={() => cancelTicket(data.ticket_id)}
							disabled={loadingCancel}
						/>
					</div>
				</PunchupModal>
			)}
			{showUncancelModal && data.status?.toLowerCase() === 'canceled' && (
				<PunchupModal title={t('attendee-actions-uncancel-ticket', 'Un-Cancel Ticket')}>
					<p style={{ margin: '24px auto', textAlign: 'center', maxWidth: 340 }}>
						{t('attendee-actions-uncancel-confirm', 'Are you sure you want to un-cancel this ticket?')}
					</p>
					<div style={{ display: 'flex', justifyContent: 'center', gap: 16 }}>
						<PunchupButton
							variant="secondary"
							text={t('cancel', 'Cancel')}
							onClick={() => setShowUncancelModal(false)}
							disabled={loadingUncancel}
						/>
						<PunchupButton
							variant="primary"
							text={loadingUncancel ? t('uncancelling', 'Un-Cancelling...') : t('confirm', 'Confirm')}
							onClick={() => uncancelTicket(data.ticket_id)}
							disabled={loadingUncancel}
						/>
					</div>
				</PunchupModal>
			)}
			{showRefundModal && (
				<PunchupModal title={t('attendee-actions-refund-ticket', 'Refund Ticket')}>
					<p
						style={{
							margin: '24px auto 8px',
							textAlign: 'center',
							wordBreak: 'break-word',
							whiteSpace: 'normal',
							maxWidth: 340,
							minHeight: 48,
							overflowWrap: 'break-word',
						}}
					>
						{t(
							'attendee-actions-refund-confirm',
							'Are you sure you want to refund this ticket? This action cannot be undone.'
						)}
					</p>
					<textarea
						placeholder={t('attendee-actions-refund-reason', 'Reason for refund (optional)')}
						value={refundReason}
						onChange={(e) => setRefundReason(e.target.value)}
						style={{
							width: '100%',
							minHeight: 60,
							margin: '8px 0 24px',
							borderRadius: 8,
							border: '1px solid #e0e0e0',
							padding: 8,
							fontSize: 15,
						}}
					/>
					<div style={{ display: 'flex', justifyContent: 'center', gap: 16 }}>
						<PunchupButton
							variant="secondary"
							text={t('cancel', 'Cancel')}
							onClick={() => setShowRefundModal(false)}
							disabled={loadingRefund}
						/>
						<PunchupButton
							variant="primary"
							text={loadingRefund ? t('refunding', 'Refunding...') : t('confirm', 'Confirm')}
							onClick={() => refundTicket({ ticketId: data.ticket_id, amount: data.amount, description: refundReason })}
							disabled={loadingRefund}
						/>
					</div>
				</PunchupModal>
			)}
			{showValidateModal && (
				<ConfirmDialog
					variant={data.is_validated ? 'warning' : 'success'}
					title={
						data.is_validated
							? t('attendee-actions-revalidate-title', 'Re-validate ticket')
							: t('attendee-actions-validate-title', 'Validate ticket')
					}
					description={
						data.is_validated
							? t(
									'attendee-actions-revalidate-confirm',
									'This ticket has already been scanned. Re-validating will record another check-in.'
							  )
							: t(
									'attendee-actions-validate-confirm',
									'This will check the attendee in and mark the ticket as scanned. Continue?'
							  )
					}
					confirmLabel={loadingValidate ? t('validating', 'Validating...') : t('confirm', 'Confirm')}
					cancelLabel={t('cancel', 'Cancel')}
					loading={loadingValidate}
					onCancel={() => setShowValidateModal(false)}
					onConfirm={() => validateTicket({ ticketId: data.ticket_id, eventId })}
				/>
			)}
			{showUndoCheckInModal && (
				<ConfirmDialog
					variant="warning"
					title={t('attendee-actions-undo-check-in-title', 'Undo this check-in?')}
					description={t(
						'attendee-actions-undo-check-in-confirm',
						'The ticket will show as not scanned for this show and can be scanned again.'
					)}
					confirmLabel={
						loadingUndoCheckIn ? t('undoing', 'Undoing...') : t('attendee-actions-undo-check-in', 'Undo check-in')
					}
					cancelLabel={t('cancel', 'Cancel')}
					loading={loadingUndoCheckIn}
					onCancel={() => setShowUndoCheckInModal(false)}
					onConfirm={() => undoCheckIn({ ticketId: data.ticket_id, eventId })}
				/>
			)}
			{delegatingSwitch && (
				<SwitchTicketDelegator
					data={data}
					onDone={() => setDelegatingSwitch(false)}
					transactionTickets={transactionTickets}
				/>
			)}
			{showNotesModal &&
				// Portal to document.body: FanNotesModal is built on PunchupDialogue, which in the iframe
				// switches its overlay to position:absolute anchored to the visible viewport. Rendered inline
				// inside the position:relative Container it would anchor to that cell instead and jump out of
				// view a frame after opening (the flash/close). The other modals here use Form/PunchupModal
				// (position:fixed) and are unaffected. Matches the green AttendeeActions render.
				ReactDOM.createPortal(
					<FanNotesModal
						userId={data.user_id}
						fanName={data.full_name || data.email}
						onClose={() => setShowNotesModal(false)}
					/>,
					document.body
				)}
		</Styled.Container>
	);
};

const TRANSACTION_TYPE_LABELS: Record<string, string> = {
	transferred: 'Transferred',
	sale_canceled: 'Canceled',
	switched_to: 'Switched to',
	switched_from: 'Switched From',
	refunded: 'Refunded',
	primary_sold: 'Sale',
};

const useTicketDetails = (ticketId?: number) => {
	return useQuery({
		queryKey: ['ticket-details', ticketId],
		queryFn: () => (ticketId ? getData(`/tickets/${ticketId}`) : Promise.resolve(null)),
		enabled: !!ticketId,
	});
};

const HistoryModalContent = ({ ticketId, onClose }: { ticketId: number; onClose: () => void }) => {
	const { data: historyData, isLoading: historyLoading, error: historyError } = useTicketHistory(ticketId);
	const { data: ticketDetails, isLoading: ticketLoading } = useTicketDetails(ticketId);

	if (historyLoading || ticketLoading) return <div style={{ padding: 24, textAlign: 'center' }}>Loading...</div>;
	if (historyError) return <div style={{ padding: 24, color: 'red', textAlign: 'center' }}>Error loading history</div>;
	if (!historyData || !Array.isArray(historyData) || historyData.length === 0) {
		return <div style={{ padding: 24, textAlign: 'center' }}>No history found</div>;
	}

	const isTicketValidated = ticketDetails?.validated || false;
	const validatedTimes = ticketDetails?.validated_times || 0;
	const promoCodeName = historyData?.[0].UsedPromotion || '';

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				height: '100%',
				maxHeight: '70vh',
				background: '#f7f8fa',
				borderRadius: 8,
			}}
		>
			<div
				style={{
					background: '#fff',
					borderRadius: 8,
					margin: '12px',
					padding: 16,
					boxShadow: '0 1px 4px #0001',
					borderLeft: '4px solid #1a73e8',
					flexShrink: 0,
				}}
			>
				<div style={{ fontWeight: 600, fontSize: 16, marginBottom: 8, color: '#1a73e8' }}>Ticket Information</div>
				<div style={{ fontSize: 14, color: '#444', marginBottom: 4 }}>
					<strong>Scanned Status:</strong> {isTicketValidated ? 'Scanned' : 'Not Scanned'}
				</div>
				{isTicketValidated && (
					<div style={{ fontSize: 14, color: '#444', marginBottom: 4 }}>
						<strong>Times Scanned:</strong> {validatedTimes}
					</div>
				)}
				{promoCodeName !== '' && (
					<div style={{ fontSize: 14, color: '#444', marginBottom: 4 }}>
						<strong>Promotion Applied:</strong> {promoCodeName || 'No promotion applied'}
					</div>
				)}
			</div>

			<div
				style={{
					flex: 1,
					overflow: 'auto',
					padding: '0 12px',
					maxHeight: 'calc(70vh - 200px)',
				}}
			>
				{historyData.map((item: any) => {
					const t = item.Transaction;
					const u = item.TransactionUser;
					let icon = <Clock size={18} style={{ marginRight: 8, color: '#888' }} />;
					if (t.transaction_type === 'transferred') icon = <Send size={18} style={{ marginRight: 8, color: '#888' }} />;
					if (t.transaction_type === 'refunded')
						icon = <RotateCcw size={18} style={{ marginRight: 8, color: '#888' }} />;
					if (t.transaction_type === 'primary_sold')
						icon = <DollarSign size={18} style={{ marginRight: 8, color: '#888' }} />;
					if (t.transaction_type === 'switched_to' || t.transaction_type === 'switched_from')
						icon = <ArrowRightLeft size={18} style={{ marginRight: 8, color: '#888' }} />;
					const label = TRANSACTION_TYPE_LABELS[t.transaction_type] || t.transaction_type;
					return (
						<div
							key={t.ID}
							style={{
								background: '#fff',
								borderRadius: 8,
								margin: '12px 0',
								padding: 16,
								boxShadow: '0 1px 4px #0001',
								wordWrap: 'break-word',
								overflowWrap: 'break-word',
							}}
						>
							<div
								style={{
									display: 'flex',
									alignItems: 'flex-start',
									marginBottom: 8,
									flexWrap: 'wrap',
									gap: '8px',
								}}
							>
								<div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
									{icon}
									<span style={{ fontWeight: 600, marginRight: 8 }}>{label}</span>
									<span style={{ color: '#888', marginRight: 8 }}>#{t.ID}</span>
								</div>
								<span style={{ color: '#888', fontSize: '14px' }}>{new Date(t.CreatedAt).toLocaleString()}</span>
							</div>
							<div
								style={{
									fontSize: 15,
									color: '#444',
									wordBreak: 'break-word',
									overflowWrap: 'break-word',
									lineHeight: '1.4',
									display: 'flex',
									flexWrap: 'wrap',
									gap: '4px',
									alignItems: 'baseline',
								}}
							>
								<span>
									User: <b>{u.full_name}</b>
								</span>
								<span
									style={{
										color: '#666',
										fontSize: '14px',
										wordBreak: 'break-all',
										overflowWrap: 'break-word',
										maxWidth: '100%',
									}}
								>
									({u.email})
								</span>
							</div>
							{t.promo_amount_applied > 0 && (
								<div
									style={{
										fontSize: 14,
										color: '#28a745',
										marginTop: 8,
										wordBreak: 'break-word',
									}}
								>
									Promotion applied: ${t.promo_amount_applied}
								</div>
							)}
						</div>
					);
				})}
			</div>

			<div
				style={{
					display: 'flex',
					justifyContent: 'center',
					marginTop: 16,
					padding: '0 12px 12px',
					flexShrink: 0,
				}}
			>
				<PunchupButton variant="primary" text="Close" onClick={onClose} />
			</div>
		</div>
	);
};
